import React, { useState, useMemo, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import axios from "axios";
import { useProAuth } from "../components/ProAuthContext";
import { setWizardStep, identifyVisitor } from "../lib/timeTracker";
import StudioMiniPreview from "../components/studio/StudioMiniPreview";
import {
  ShieldCheck, UserSquare2, UsersRound, Stethoscope, CalendarClock, Video,
  FileSignature, Receipt, BookOpenCheck, Lock, Palette, Download, Check,
  ChevronRight, ChevronLeft, Sparkles, Activity, MailCheck, Mail, MonitorPlay,
  Headphones, CalendarDays, Globe, ArrowRight, Loader2, Info, CheckCircle2,
} from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ----------------------- SERVICES catalog ----------------------- */
const SERVICES = [
  {
    id: "telecons", icon: Video, title: "Téléconsultation",
    tag: "Visio LiveKit E2E",
    desc: "Consultation à distance médecin ↔ patient en visio chiffrée bout-en-bout, conforme HAS.",
    includes: ["IDM", "IDP", "BPT", "RDV", "PRE", "REA", "CCL", "FAC"],
  },
  {
    id: "teleexp", icon: Headphones, title: "Téléexpertise",
    tag: "Avis spécialiste asynchrone",
    desc: "Demande d'avis spécialisé asynchrone, documents partagés via MSSanté, sans présence patient.",
    includes: ["IDM", "IDP", "CCL", "FAC"],
  },
  {
    id: "telesurv", icon: Activity, title: "Télésurveillance",
    tag: "Suivi à distance",
    desc: "Suivi continu de patients chroniques avec dispositifs connectés (BLE, BP, SpO₂) + alertes.",
    includes: ["IDM", "IDP", "REA", "CCL"],
  },
  {
    id: "webmail", icon: MailCheck, title: "Webmail MSSanté",
    tag: "Messagerie sécurisée intégrée",
    desc: "Accès direct à la messagerie sécurisée MSSanté depuis votre plateforme, sans logiciel tiers.",
    includes: ["IDM", "CCL"],
  },
];

/* ----------------------- MetaConnect SAVOIR-FAIRE ----------------------- */
const SAVOIR_FAIRE = [
  {
    icon: CalendarDays,
    title: "Widget de prise de RDV interne",
    pill: "Propriété MetaConnect",
    desc: "Module d'agenda intégré natif à votre plateforme — disponibilités PS, créneaux téléconsultation / présentiel, vérification automatique des prérequis (1er soin présentiel, prescription valide).",
  },
  {
    icon: Globe,
    title: "Widget de RDV externe embarquable",
    pill: "Propriété MetaConnect",
    desc: "Snippet JavaScript à coller sur le site institutionnel du PS / de la CPTS. Le patient prend RDV sans quitter le site du praticien. Données synchronisées en temps réel avec votre plateforme.",
  },
  {
    icon: Mail,
    title: "Emails patients pré-programmés",
    pill: "Propriété MetaConnect",
    desc: "Bibliothèque de 12+ templates HTML responsive personnalisables : confirmation RDV, rappel 24h/2h avant, lien visio, notice consentement, questionnaire satisfaction, relance avis. Envois via Resend.",
  },
  {
    icon: ShieldCheck,
    title: "Hébergement HDS clé en main",
    pill: "Inclus",
    desc: "Vos données de santé hébergées chez notre partenaire HDS certifié français (Scaleway / OVH). Conformité PGSSI-S, RGPD, ISO 27001, Garantie DICA — aucune démarche d'hébergement à votre charge.",
  },
];

/* ----------------------- CDC BLOCKS with questions ----------------------- */
const BLOCKS = {
  IDM: {
    icon: ShieldCheck, title: "Identification & Auth des PS",
    summary: "Authentification forte via Pro Santé Connect + référencement RPPS / FINESS.",
    refs: ["PSC", "RPPS", "FINESS", "e-CPS"],
    questions: [
      { id: "psc_cards", type: "radio", label: "Vos PS disposent-ils tous d'une carte CPx ou e-CPS ?", helper: "La CPx (carte CPS physique) ou e-CPS (carte dématérialisée) permet l'authentification forte imposée par la réglementation e-santé. Si vous ne savez pas, choisissez « Je ne sais pas ».", options: ["Oui, tous", "Partiellement", "Non / à acquérir", "Je ne sais pas"] },
      { id: "structure_type", type: "checkbox", label: "Type de structure(s) à enregistrer", options: ["CPTS", "MSP", "Cabinet libéral", "Clinique", "EHPAD", "Groupe médical"] },
      { id: "ps_count", type: "radio", label: "Combien de PS utiliseront la plateforme ?", options: ["1-5", "6-20", "21-50", "51-200", "200+"] },
      { id: "finess_ror", type: "text", label: "N° FINESS / ROR de votre structure principale", helper: "Le numéro FINESS identifie votre établissement de santé. Laissez vide si vous ne l'avez pas — nous le retrouverons ensemble.", optional: true },
    ],
  },
  IDP: {
    icon: UserSquare2, title: "Identification Patient & Dossier",
    summary: "Qualification INS + identitovigilance RNIV + dossier patient.",
    refs: ["INS v2.1", "RNIV", "INSi"],
    questions: [
      { id: "patient_base", type: "radio", label: "Avez-vous une base patient existante à migrer ?", options: ["Oui (CSV/Excel)", "Oui (API LGC)", "Non, on démarre à zéro"] },
      { id: "patient_count", type: "radio", label: "Volume patient estimé en année 1", options: ["<500", "500-2000", "2000-10000", "10000+"] },
      { id: "ins_workflow", type: "radio", label: "Comment qualifiez-vous l'INS aujourd'hui ?", helper: "L'INS (Identifiant National de Santé) est le numéro unique du patient. La qualification consiste à vérifier son identité selon le référentiel RNIV. Si vous démarrez, choisissez « Pas encore en place ».", options: ["Manuellement", "Via mon LGC actuel", "Pas encore en place"] },
    ],
  },
  BPT: {
    icon: BookOpenCheck, title: "Bonnes pratiques HAS",
    summary: "Checklist pertinence + double consentement + mode dégradé.",
    refs: ["HAS · BPT 2021", "Référentiel télésoin ANS"],
    new: true,
    questions: [
      { id: "has_trained", type: "radio", label: "Vos PS sont-ils formés à la téléconsultation HAS ?", options: ["Oui tous", "Partiellement", "Non, à prévoir"] },
      { id: "specialties", type: "textarea", label: "Quelles spécialités / actes médicaux concernés en priorité ?" },
    ],
  },
  RDV: {
    icon: CalendarClock, title: "Planification & Rendez-vous",
    summary: "Widget MetaConnect agenda interne + widget embarquable externe.",
    refs: ["Agenda MetaConnect", "iCal", "Widget public"],
    questions: [
      { id: "agenda_tool", type: "radio", label: "Agenda connecté actuellement utilisé ?", options: ["Doctolib", "Maiia", "KelDoc", "Clickdoc", "Autre", "Aucun"] },
      { id: "use_widget", type: "checkbox", label: "Widgets MetaConnect à activer", options: ["Widget interne (plateforme)", "Widget externe (site internet)", "Synchronisation avec agenda existant"] },
      { id: "site_embed", type: "text", label: "URL du site sur lequel intégrer le widget RDV externe" },
      { id: "online_booking", type: "radio", label: "Prise de RDV en ligne par le patient ?", options: ["Libre accès", "Après accord PS", "Uniquement par PS"] },
    ],
  },
  PRE: {
    icon: MonitorPlay, title: "Préparation de session",
    summary: "Salle d'attente LiveKit + check technique + emails patients pré-programmés.",
    refs: ["LiveKit E2E", "WebRTC", "PGSSI-S"],
    questions: [
      { id: "reminder_channel", type: "checkbox", label: "Canaux d'emails / rappels patient activés", options: ["Email confirmation", "Email rappel 24h", "Email rappel 2h", "SMS rappel (surcoût)", "Email post-session satisfaction"] },
      { id: "patient_tech_level", type: "radio", label: "Profil technique de vos patients", options: ["Très autonome", "Variable", "Faible (seniors, aidants)"] },
    ],
  },
  REA: {
    icon: UsersRound, title: "Réalisation de l'acte",
    summary: "Visio LiveKit chiffrée + chat + dispositifs connectés (télésurveillance).",
    refs: ["LiveKit E2E", "HDS · agréé", "REA.1.2 ANS"],
    questions: [
      { id: "max_concurrent", type: "radio", label: "Téléconsultations simultanées max prévues", options: ["1-5", "6-20", "21-100", "100+"] },
      { id: "advanced_features", type: "checkbox", label: "Fonctions avancées souhaitées", options: ["Partage d'écran", "Capture image médicale", "Dispositif connecté (BLE/BP/SpO₂)", "Enregistrement (HDS, surcoût)"] },
    ],
  },
  CCL: {
    icon: FileSignature, title: "Conclusion & Documents",
    summary: "Signature électronique + transmission DMP + MSSanté webmail intégré.",
    refs: ["DMP · CDA R2", "MSSanté", "eIDAS"],
    questions: [
      { id: "mss_existing", type: "radio", label: "Vos PS disposent-ils déjà d'une adresse MSSanté active ?", options: ["Oui tous", "Partiellement", "Non — création via MetaConnect"] },
      { id: "report_template", type: "radio", label: "Modèles de comptes rendus existants ?", options: ["Oui (à reproduire)", "Non, à créer ensemble"] },
      { id: "lgc_target", type: "text", label: "LGC / DPI cible pour l'export", helper: "Indiquez le nom de votre logiciel de gestion de cabinet (ex : Weda, Hellodoc, Médistory, Crossway…). Laissez vide si vous n'en avez pas encore.", optional: true },
    ],
  },
  FAC: {
    icon: Receipt, title: "Compte rendu d'activité & Facturation",
    summary: "Push API vers LGC + droits patient ADRi/CDRi.",
    refs: ["FSE", "ADRi / CDRi", "NGAP · CCAM"],
    questions: [
      { id: "billing_target", type: "radio", label: "Mode de facturation cible", options: ["Indirect via LGC", "FSE directe (Phase 3)", "Privé hors AMO"] },
      { id: "adri_cdri", type: "radio", label: "Interrogation ADRi/CDRi des droits patient ?", helper: "ADRi et CDRi sont des services de l'Assurance Maladie qui vérifient automatiquement les droits du patient avant la consultation. Recommandé si vous faites de la FSE directe.", options: ["Oui", "Non"] },
    ],
  },
};

/* ----------------------- COMPONENT ----------------------- */
const STEPS = [
  { id: 0, label: "Services", desc: "Choisissez les modules dont vous avez besoin" },
  { id: 1, label: "Structure", desc: "Décrivez votre cabinet ou votre CPTS" },
  { id: 2, label: "Identité", desc: "Logo, nom et couleurs de votre plateforme" },
  { id: 3, label: "Savoir-faire", desc: "Vos spécialités et votre équipe de PS" },
  { id: 4, label: "Configuration", desc: "Workflows, agenda, intégrations" },
  { id: 5, label: "Récapitulatif", desc: "Téléchargez votre cahier des charges" },
];

export default function Studio() {
  const { user, loading: authLoading } = useProAuth();
  const navigate = useNavigate();
  const authGated = !authLoading && !user;
  const goLogin = () => navigate("/mssante/connexion?redirect=/studio");
  const [step, setStep] = useState(0);
  const [services, setServices] = useState([]);
  const [focusService, setFocusService] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const [account, setAccount] = useState({ status: "none", message: null });
  const [brand, setBrand] = useState({
    name: "", structureType: "", psCount: "",
    contactName: "", contactEmail: "", contactPhone: "",
    color: "#6EE7C5", accent: "#7FB3FF", font: "Inter", logoUrl: "",
    domainType: "subdomain", domain: "", rdvUrl: "",
  });
  const [answers, setAnswers] = useState({}); // { blockId: { qId: value } }

  // Auto-fill identity fields from the authenticated MetaConnect session (single sign-on).
  useEffect(() => {
    if (!user) return;
    const full = `${user.first_name || ""} ${user.last_name || ""}`.trim();
    setBrand((b) => ({
      ...b,
      contactName: b.contactName || full,
      contactEmail: b.contactEmail || user.email || "",
      contactPhone: b.contactPhone || user.phone || "",
    }));
  }, [user]);

  // Report the current wizard step to the anonymous time tracker so admins can
  // see how long each prospect spends per step. Reset on unmount.
  useEffect(() => {
    setWizardStep(step);
    return () => setWizardStep(null);
  }, [step]);

  // Link the anonymous visitor id to this prospect's email as soon as we have
  // one (either from the auth session or typed into step 1).
  useEffect(() => {
    const email = (brand.contactEmail || "").trim();
    if (email && email.includes("@")) {
      identifyVisitor({ email, pro_user_id: user?.id });
    }
  }, [brand.contactEmail, user?.id]);

  // Compute relevant blocks based on selected services
  const relevantBlocks = useMemo(() => {
    const set = new Set();
    services.forEach((sid) => {
      const s = SERVICES.find((x) => x.id === sid);
      if (s) s.includes.forEach((b) => set.add(b));
    });
    return Array.from(set);
  }, [services]);

  const toggleService = (id) => {
    setServices((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      setFocusService(next.includes(id) ? id : next[next.length - 1] || null);
      return next;
    });
  };

  const setAnswer = (blockId, qId, value) => {
    setAnswers((prev) => ({ ...prev, [blockId]: { ...(prev[blockId] || {}), [qId]: value } }));
  };

  const canNext = useMemo(() => {
    if (step === 0) return services.length > 0;
    if (step === 1) {
      // The contact identity is enforced by the authenticated session — we only
      // need the structure name and a structure type/size to move forward.
      return brand.name.trim().length > 1 && !!brand.structureType && !!brand.psCount;
    }
    return true;
  }, [step, services, brand]);

  const progress = Math.round(((step + 1) / STEPS.length) * 100);

  // B1 — Total questions for the sticky topbar counter.
  // Includes only the config questions actually surfaced for the user (Step 4 modules).
  const totalQuestions = useMemo(
    () => relevantBlocks.reduce((acc, bid) => acc + (BLOCKS[bid]?.questions.length || 0), 0),
    [relevantBlocks]
  );
  const answeredQuestions = useMemo(() => {
    let n = 0;
    relevantBlocks.forEach((bid) => {
      const blockAns = answers[bid] || {};
      BLOCKS[bid].questions.forEach((q) => {
        const v = blockAns[q.id];
        if (Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null && String(v).trim() !== "") n += 1;
      });
    });
    return n;
  }, [relevantBlocks, answers]);

  // Build the API payload (shared by export & preview actions)
  const buildPayload = () => {
    const sections = relevantBlocks.map((bid) => {
      const b = BLOCKS[bid];
      const ans = answers[bid] || {};
      return {
        id: bid, title: b.title, summary: b.summary,
        answers: b.questions.map((q) => ({ question: q.label, answer: ans[q.id] ?? null })),
      };
    });
    return {
      brand: { ...brand, contactEmail: (brand.contactEmail || "").trim() },
      services: services.map((id) => {
        const s = SERVICES.find((x) => x.id === id);
        return { id, title: s?.title || id };
      }),
      sections,
    };
  };

  // Validate required fields and submit. Returns the API response or null.
  const submitCdc = async () => {
    const email = (brand.contactEmail || "").trim();
    if (!brand.name || !email.includes("@")) {
      toast.error("Nom de structure et email valide requis.");
      setStep(1);
      return null;
    }
    const { data } = await axios.post(`${API_BASE}/studio/cdc`, buildPayload(), { timeout: 30000 });
    if (data?.id) setPreviewId(data.id);
    return data;
  };

  // Open preview in a new tab — saves silently first if not already done
  const [previewLoading, setPreviewLoading] = useState(false);
  const handlePreview = async () => {
    if (previewLoading) return;
    if (!user) { goLogin(); return; }
    // If already submitted, just open the existing preview
    if (previewId) {
      window.open(`/studio/preview/${previewId}`, "_blank", "noopener");
      return;
    }
    setPreviewLoading(true);
    try {
      const data = await submitCdc();
      if (data?.id) {
        setSubmitted(true);
        toast.success("Aperçu prêt — ouverture dans un nouvel onglet.");
        window.open(`/studio/preview/${data.id}`, "_blank", "noopener");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Erreur inconnue";
      toast.error(`Impossible de générer l'aperçu : ${detail}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Inline editor entry — creates a draft and opens the sandbox in editor mode.
  // The pro can answer the configuration questions contextually while navigating
  // their generated platform; PDF generation happens at "Finaliser" time.
  const [inlineLoading, setInlineLoading] = useState(false);
  const handleStartInlineEditor = async () => {
    if (inlineLoading) return;
    if (!user) { goLogin(); return; }
    if (services.length === 0) {
      toast.error("Sélectionnez au moins un service pour commencer.");
      return;
    }
    setInlineLoading(true);
    try {
      const payload = {
        brand: { ...brand, contactEmail: (brand.contactEmail || "").trim() },
        services: services.map((id) => {
          const s = SERVICES.find((x) => x.id === id);
          return { id, title: s?.title || id };
        }),
      };
      const { data } = await axios.post(`${API_BASE}/studio/drafts`, payload, { timeout: 15000 });
      if (data?.id) {
        toast.success("Aperçu en direct prêt — répondez en navigant !");
        window.open(`/studio/preview/${data.id}?mode=editor`, "_blank", "noopener");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Erreur inconnue";
      toast.error(`Impossible de démarrer l'aperçu live : ${detail}`);
    } finally {
      setInlineLoading(false);
    }
  };

  const handleExport = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const data = await submitCdc();
      if (!data) return;

      if (data?.pdf_base64) {
        // Trigger immediate download of the PDF
        const bin = atob(data.pdf_base64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        const blob = new Blob([bytes], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = data.filename || `CDC-MetaConnect-${(brand.name || "projet").replace(/\s+/g, "-")}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }

      setSubmitted(true);
      // SEC-003: response is uniform — we don't know (and don't want to know) whether the
      // email was already registered. Display a single, neutral banner.
      if (data?.account_status === "submitted") {
        toast.success("Espace professionnel prêt. Connectez-vous pour continuer.");
      } else if (data?.account_status === "invalid" && data?.account_message) {
        toast.error(data.account_message);
      }
      setAccount({ status: data?.account_status || "none", message: data?.account_message || null });
      if (data?.email_sent) {
        toast.success("CDC envoyé par email et téléchargé. À très vite !");
      } else {
        toast.success("CDC téléchargé. Notre équipe vous recontacte.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Erreur inconnue";
      toast.error(`Échec de l'envoi : ${detail}`);
    } finally {
      setSubmitting(false);
    }
  };

  // SEO note: the page renders publicly (hero + gate card). The wizard itself
  // requires the MetaConnect pro session — unauthenticated CTAs route to login.

  return (
    <div className="min-h-screen" style={{ background: "#F8FAFC", color: "#0F2B5B" }} data-testid="page-studio">
      <SEO
        title="MetaConnect Studio — Configurez votre plateforme de télésanté"
        description="Wizard guidé pour construire votre plateforme de téléconsultation, téléexpertise, télésurveillance et webmail MSSanté. Conforme ANS, HAS, RGPD, HDS."
        canonical="/studio"
      />

      {/* HERO — Phase A — Split #0F2B5B / #ffffff / #4FC3F7 */}
      <section className="relative" data-testid="studio-hero" style={{ background: "#ffffff" }}>
        {step === 0 && (
        <div className="grid lg:grid-cols-2 min-h-[560px]">
          {/* LEFT — Navy column */}
          <div
            className="px-6 sm:px-10 lg:px-16 py-16 lg:py-20 flex flex-col justify-center"
            style={{ background: "#0F2B5B" }}
          >
            <div
              className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.16em]"
              style={{
                background: "rgba(79,195,247,0.12)",
                border: "1px solid rgba(79,195,247,0.30)",
                color: "#4FC3F7",
                fontWeight: 500,
              }}
              data-testid="studio-hero-badge"
            >
              <Sparkles size={13} />
              MetaConnect Studio · Configurateur no-code
            </div>

            <h1
              data-testid="studio-hero-h1"
              className="mt-6 font-display"
              style={{
                color: "#ffffff",
                fontSize: "clamp(28px, 4vw, 44px)",
                fontWeight: 600,
                lineHeight: 1.12,
                letterSpacing: "-0.015em",
              }}
            >
              Créez votre plateforme de télémédecine en{" "}
              <span style={{ color: "#4FC3F7" }}>{STEPS.length} étapes</span>.
            </h1>

            <p className="mt-5 max-w-md" style={{ color: "#93B4D4", fontSize: 15.5, lineHeight: 1.55 }}>
              Aucune expertise technique requise. Répondez aux questions, visualisez en temps réel
              et recevez votre cahier des charges PDF.
            </p>

            <ul className="mt-6 space-y-2.5">
              {[
                "Sans code, sans intégrateur",
                "Conformité ANS, HDS et RGPD intégrée",
                "Aperçu sandbox instantané",
              ].map((b) => (
                <li key={b} className="flex items-center gap-3" style={{ color: "#B8D4E8", fontSize: 14 }}>
                  <span
                    style={{
                      width: 6, height: 6, borderRadius: "50%",
                      background: "#4FC3F7", flexShrink: 0,
                    }}
                  />
                  {b}
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                onClick={() => {
                  if (authGated) { goLogin(); return; }
                  document.getElementById("studio-wizard")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                data-testid="studio-hero-cta-start"
                className="inline-flex items-center gap-2 px-7 py-3 transition-all hover:translate-y-[-1px]"
                style={{
                  background: "#4FC3F7",
                  color: "#0F2B5B",
                  fontWeight: 600,
                  fontSize: 14.5,
                  borderRadius: 8,
                  border: 0,
                  cursor: "pointer",
                  boxShadow: "0 12px 28px rgba(79,195,247,0.32)",
                }}
              >
                Commencer : choisir mes services ↓
              </button>
              <button
                onClick={handlePreview}
                disabled={previewLoading}
                data-testid="studio-hero-cta-preview"
                className="inline-flex items-center gap-2 px-6 py-3 transition-all hover:translate-y-[-1px] disabled:opacity-60"
                style={{
                  background: "transparent",
                  color: "#BFE7FB",
                  fontWeight: 500,
                  fontSize: 14,
                  borderRadius: 8,
                  border: "1px solid rgba(79,195,247,0.45)",
                  cursor: previewLoading ? "wait" : "pointer",
                }}
              >
                {previewLoading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" />Génération…</>
                ) : (
                  <>Voir mon aperçu en direct <ArrowRight size={15} /></>
                )}
              </button>
            </div>

            {/* Compliance footer inline */}
            <div
              className="mt-10 -mx-6 sm:-mx-10 lg:-mx-16 px-6 sm:px-10 lg:px-16 py-3.5 flex flex-wrap items-center gap-x-6 gap-y-2"
              style={{ background: "rgba(0,0,0,0.20)" }}
            >
              {["ANS", "HDS", "RGPD", "CI-SIS", "CNDA"].map((b) => (
                <span key={b} className="inline-flex items-center gap-1.5" style={{ fontSize: 11, color: "#93B4D4", letterSpacing: "0.04em" }}>
                  <ShieldCheck size={11} style={{ color: "#4FC3F7" }} />
                  {b}
                </span>
              ))}
            </div>
          </div>

          {/* RIGHT — White column */}
          <div
            className="px-6 sm:px-10 lg:px-14 py-16 lg:py-20 flex flex-col justify-center"
            style={{
              background: "#ffffff",
              borderLeft: "0.5px solid #E8EDF5",
            }}
          >
            <div className="flex items-center gap-2 mb-7" data-testid="studio-hero-steps-label">
              <span style={{ width: 3, height: 22, background: "#4FC3F7", borderRadius: 2 }} />
              <span style={{
                fontSize: 11, color: "#4FC3F7", letterSpacing: "0.08em",
                textTransform: "uppercase", fontWeight: 600,
              }}>
                Les {STEPS.length} étapes
              </span>
            </div>

            <ol className="relative space-y-4 pl-1" data-testid="studio-hero-steps-list">
              {STEPS.map((s, i) => {
                const passed = i < step;
                const active = i === step;
                const showConnector = i < STEPS.length - 1;
                // Style of the number badge.
                const badgeStyle = active
                  ? { background: "#0F2B5B", color: "#ffffff", border: "1px solid #0F2B5B" }
                  : passed
                    ? { background: "#0F2B5B", color: "#ffffff", border: "1px solid #0F2B5B" }
                    : { background: "#F0F9FF", color: "#0F2B5B", border: "1px solid #BAE6FD" };
                return (
                  <li
                    key={s.id}
                    className="relative flex items-start gap-4 group cursor-pointer"
                    data-testid={`studio-hero-step-${s.id}`}
                    onClick={() => {
                      if (i <= step) setStep(i);
                      document.getElementById("studio-wizard")?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                  >
                    {showConnector && (
                      <span
                        aria-hidden="true"
                        style={{
                          position: "absolute",
                          left: 17, top: 36, bottom: -16,
                          width: 1, background: "#E8EDF5",
                        }}
                      />
                    )}
                    <div
                      className="grid place-items-center shrink-0"
                      style={{
                        width: 36, height: 36, borderRadius: "50%",
                        fontSize: 14, fontWeight: 600,
                        transition: "all .15s ease",
                        ...badgeStyle,
                      }}
                    >
                      {i + 1}
                    </div>
                    <div className="pt-0.5 pb-1">
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          color: "#0F2B5B",
                          letterSpacing: "-0.005em",
                        }}
                      >
                        {s.label}
                        {active && (
                          <span className="ml-2 inline-flex items-center" style={{
                            fontSize: 10, color: "#4FC3F7", letterSpacing: "0.06em",
                            textTransform: "uppercase", fontWeight: 600,
                          }}>
                            En cours
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
                        {s.desc}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>

            <Link
              to="/demo"
              data-testid="studio-hero-cta-demo"
              className="mt-8 inline-flex items-center justify-center gap-2 px-5 py-3 transition-all hover:translate-y-[-1px]"
              style={{
                background: "#EFF6FF",
                border: "1px solid #BFDBFE",
                color: "#0F2B5B",
                fontSize: 13.5,
                fontWeight: 500,
                borderRadius: 8,
                textDecoration: "none",
              }}
            >
              Planifier une démo avec un expert <ArrowRight size={14} />
            </Link>
          </div>
        </div>
        )}

        {/* B1 — STICKY TOPBAR TUNNEL — palette navy/white/mint */}
        {!authGated && (<>
        <div
          data-testid="studio-topbar-tunnel"
          style={{
            background: "#ffffff",
            borderBottom: "0.5px solid #E8EDF5",
            height: 56,
            padding: "0 24px",
            position: "sticky",
            top: 0,
            zIndex: 50,
            display: "flex",
            alignItems: "center",
            gap: 16,
          }}
        >
          {/* Left — back link + brand */}
          <div className="flex items-center gap-3 shrink-0">
            <Link
              to="/"
              data-testid="topbar-back"
              style={{ color: "#64748B", fontSize: 13, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
            >
              <ChevronLeft size={14} /> Studio
            </Link>
            <span style={{ width: 1, height: 18, background: "#E8EDF5" }} aria-hidden="true" />
            <span style={{ color: "#0F2B5B", fontSize: 13.5, fontWeight: 500 }}>MetaConnect Studio</span>
          </div>

          {/* Center — 6-step inline stepper */}
          <div className="flex-1 hidden md:flex items-center justify-center gap-2 min-w-0" data-testid="topbar-stepper">
            {STEPS.map((s, i) => {
              const passed = i < step;
              const active = i === step;
              const future = i > step;
              const showConn = i < STEPS.length - 1;
              const dot = active
                ? { background: "#0F2B5B", color: "#ffffff", border: "1px solid #0F2B5B" }
                : passed
                  ? { background: "#4FC3F7", color: "#0F2B5B", border: "1px solid #4FC3F7" }
                  : { background: "#F0F9FF", color: "#94A3B8", border: "0.5px solid #BAE6FD" };
              const labelColor = active ? "#0F2B5B" : passed ? "#64748B" : "#94A3B8";
              const labelWeight = active ? 500 : 400;
              const connColor = passed || active ? "#4FC3F7" : "#E2E8F0";
              return (
                <React.Fragment key={s.id}>
                  <button
                    type="button"
                    onClick={() => {
                      if (i <= step) setStep(i);
                      document.getElementById("studio-wizard")?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                    disabled={future}
                    data-testid={`topbar-step-${s.id}`}
                    className="flex items-center gap-1.5 group"
                    style={{
                      background: "transparent",
                      border: 0,
                      padding: 0,
                      cursor: future ? "default" : "pointer",
                    }}
                    aria-label={`Étape ${i + 1} — ${s.label}`}
                  >
                    <span
                      style={{
                        width: 24, height: 24, borderRadius: "50%",
                        display: "grid", placeItems: "center",
                        fontSize: 11.5, fontWeight: 600,
                        transition: "all .15s ease",
                        ...dot,
                      }}
                    >
                      {passed ? <Check size={12} strokeWidth={3} /> : i + 1}
                    </span>
                    <span
                      className="hidden lg:inline"
                      style={{ fontSize: 12, color: labelColor, fontWeight: labelWeight, letterSpacing: "-0.005em" }}
                    >
                      {s.label}
                    </span>
                  </button>
                  {showConn && (
                    <span
                      aria-hidden="true"
                      style={{
                        flex: "0 1 28px",
                        height: 1,
                        background: connColor,
                        minWidth: 12,
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Right — counter + see all */}
          <div className="flex items-center gap-3 shrink-0 ml-auto md:ml-0">
            {totalQuestions > 0 && (
              <span style={{ color: "#64748B", fontSize: 13, fontVariantNumeric: "tabular-nums" }} data-testid="topbar-questions-counter">
                {answeredQuestions} / {totalQuestions} questions
              </span>
            )}
            <button
              type="button"
              onClick={() => setStep(4)}
              data-testid="topbar-see-all"
              style={{
                background: "#F1F5F9",
                border: 0,
                color: "#0F2B5B",
                borderRadius: 8,
                padding: "8px 14px",
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
              className="transition-colors hover:bg-[#E2E8F0]"
            >
              Voir toutes les questions <ArrowRight size={13} />
            </button>
          </div>
        </div>

        {/* Progress bar — anchored below topbar, full width (kept for visual reinforcement) */}
        <div className="border-b" style={{ background: "#F8FAFC", borderColor: "#E8EDF5" }}>
          <div className="max-w-6xl mx-auto px-5 lg:px-8 py-3 flex items-center gap-4">
            <span style={{ fontSize: 11, color: "#64748B", letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 600 }}>
              Progression
            </span>
            <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "#E2E8F0" }}>
              <div
                style={{
                  width: `${progress}%`,
                  height: "100%",
                  background: "#4FC3F7",
                  borderRadius: 999,
                  transition: "width 250ms ease-out",
                }}
              />
            </div>
            <span style={{ fontSize: 13, color: "#0F2B5B", fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
              {step + 1} / {STEPS.length}
            </span>
          </div>
        </div>
        </>)}
      </section>

      {/* STEP CONTENT */}
      <section id="studio-wizard" className="relative py-12 px-5 lg:px-8 min-h-[500px] scroll-mt-24">
        <div className="max-w-5xl mx-auto">

          {/* AUTH GATE — public SEO-friendly card shown to unauthenticated visitors */}
          {authGated && (
            <div
              className="mx-auto max-w-2xl text-center p-8 lg:p-12"
              style={{ background: "#ffffff", border: "0.5px solid #E8EDF5", borderRadius: 16, boxShadow: "0 12px 32px -16px rgba(15,43,91,0.15)" }}
              data-testid="studio-auth-gate"
            >
              <div className="mx-auto w-14 h-14 rounded-2xl grid place-items-center" style={{ background: "#F0F9FF", border: "0.5px solid #BAE6FD" }}>
                <Lock className="w-6 h-6" style={{ color: "#0F2B5B" }} />
              </div>
              <h2 className="font-display text-2xl lg:text-3xl mt-5" style={{ color: "#0F2B5B", fontWeight: 600 }}>
                Connectez-vous pour configurer votre plateforme
              </h2>
              <p className="mt-3 text-[14.5px] leading-relaxed max-w-xl mx-auto" style={{ color: "#64748B" }}>
                Le configurateur MetaConnect Studio est gratuit et sans engagement. Un compte permet de
                sauvegarder vos réponses, générer votre aperçu sandbox personnalisé et recevoir votre
                cahier des charges PDF.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/mssante/connexion?redirect=/studio"
                  data-testid="studio-gate-login"
                  className="inline-flex items-center gap-2 transition hover:-translate-y-0.5"
                  style={{ background: "#0F2B5B", color: "#ffffff", borderRadius: 999, padding: "12px 28px", fontSize: 14, fontWeight: 600, boxShadow: "0 10px 26px -12px rgba(15,43,91,0.5)" }}
                >
                  Se connecter <ArrowRight size={15} />
                </Link>
                <Link
                  to="/mssante/inscription?redirect=/studio"
                  data-testid="studio-gate-register"
                  className="inline-flex items-center gap-2 transition hover:-translate-y-0.5"
                  style={{ background: "#ffffff", color: "#0F2B5B", border: "1px solid #CBD5E1", borderRadius: 999, padding: "11px 26px", fontSize: 14, fontWeight: 600 }}
                >
                  Créer un compte gratuit
                </Link>
              </div>
              <p className="mt-5 text-[12px]" style={{ color: "#94A3B8" }}>
                Exploration libre, sans carte bancaire — conforme ANS, hébergement HDS, RGPD.
              </p>
            </div>
          )}

          {/* STEP 0 — SERVICES */}
          {!authGated && step === 0 && (
            <div data-testid="step-services">
              <h2 className="font-display text-2xl lg:text-3xl" style={{ color: "#0F2B5B", fontWeight: 600, letterSpacing: "-0.015em" }}>Quels services voulez-vous proposer ?</h2>
              <p className="mt-2 text-[14.5px] max-w-2xl" style={{ color: "#64748B" }}>
                Sélectionnez un ou plusieurs services. Les modules à configurer s&apos;adapteront automatiquement.
              </p>
              <div className="mt-8 grid lg:grid-cols-[1fr_340px] gap-8 items-start">
                <div className="grid sm:grid-cols-2 gap-4">
                {SERVICES.map((s) => {
                  const Icon = s.icon;
                  const isOn = services.includes(s.id);
                  const isFocus = isOn && (focusService === s.id || (!focusService && services[services.length - 1] === s.id));
                  return (
                    <button
                      key={s.id}
                      onClick={() => toggleService(s.id)}
                      onMouseEnter={() => { if (isOn) setFocusService(s.id); }}
                      className="text-left p-6 transition-all duration-300 hover:-translate-y-0.5"
                      style={{
                        background: isOn ? "#F0F9FF" : "#ffffff",
                        border: `0.5px solid ${isOn ? "#4FC3F7" : "#E8EDF5"}`,
                        borderRadius: 12,
                        boxShadow: isFocus
                          ? "0 0 0 2px rgba(79,195,247,0.35), 0 8px 24px -10px rgba(79,195,247,0.3)"
                          : isOn ? "0 8px 24px -10px rgba(79,195,247,0.25)" : "0 1px 2px rgba(15,43,91,0.04)",
                      }}
                      data-testid={`service-${s.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div
                          className="w-12 h-12 rounded-xl grid place-items-center"
                          style={{ background: "#F0F9FF", border: "0.5px solid #BAE6FD" }}
                        >
                          <Icon className="w-6 h-6" style={{ color: "#0F2B5B" }} />
                        </div>
                        {isOn && (
                          <div className="w-7 h-7 rounded-full grid place-items-center" style={{ background: "#4FC3F7" }}>
                            <Check className="w-4 h-4" style={{ color: "#0F2B5B" }} strokeWidth={3} />
                          </div>
                        )}
                      </div>
                      <div className="mt-4">
                        <span
                          className="inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold"
                          style={{ background: "#F0F9FF", color: "#4FC3F7", border: "0.5px solid #BAE6FD" }}
                        >
                          {s.tag}
                        </span>
                        <div className="font-display font-semibold text-[18px] mt-2" style={{ color: "#0F2B5B" }}>{s.title}</div>
                        <p className="text-[13px] leading-relaxed mt-1.5" style={{ color: "#64748B" }}>{s.desc}</p>
                      </div>
                    </button>
                  );
                })}
                </div>
                <div className="lg:sticky lg:top-28">
                  <StudioMiniPreview services={services} focusService={focusService} />
                </div>
              </div>

              {/* Inline editor CTA — skip the tunnel and configure while navigating the live preview */}
              {services.length > 0 && (
                <div
                  className="mt-8 p-5 lg:p-6 flex flex-col lg:flex-row items-start lg:items-center gap-4 lg:gap-6"
                  style={{
                    background: "#F0F9FF",
                    border: "0.5px solid #BAE6FD",
                    borderRadius: 12,
                  }}
                  data-testid="inline-editor-cta"
                >
                  <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: "#ffffff", border: "0.5px solid #BAE6FD" }}>
                    <Sparkles className="w-5 h-5" style={{ color: "#4FC3F7" }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-semibold mb-1.5" style={{ color: "#4FC3F7" }}>
                      <span className="px-1.5 py-0.5 rounded" style={{ background: "#4FC3F7", color: "#0F2B5B" }}>Nouveau</span>
                      Configure en naviguant
                    </div>
                    <h3 className="font-display text-[18px] lg:text-[20px] leading-tight" style={{ color: "#0F2B5B" }}>
                      Voir mon aperçu en direct
                    </h3>
                    <p className="text-[12.5px] mt-1 leading-relaxed max-w-2xl" style={{ color: "#64748B" }}>
                      Sautez le tunnel ennuyeux : ouvrez la plateforme générée et répondez aux questions de configuration au moment où elles se présentent, en contexte. Vos réponses sont sauvegardées automatiquement.
                    </p>
                  </div>
                  <button
                    onClick={handleStartInlineEditor}
                    disabled={inlineLoading}
                    data-testid="start-inline-editor"
                    className="shrink-0 inline-flex items-center gap-2 transition disabled:opacity-60 disabled:cursor-wait"
                    style={{
                      background: "#0F2B5B",
                      color: "#ffffff",
                      borderRadius: 999,
                      padding: "10px 22px",
                      fontSize: 13,
                      fontWeight: 500,
                      border: 0,
                      cursor: inlineLoading ? "wait" : "pointer",
                      boxShadow: "0 8px 22px -10px rgba(15,43,91,0.45)",
                    }}
                  >
                    {inlineLoading ? "Préparation…" : "Lancer l'aperçu →"}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* STEP 1 — STRUCTURE (auth via existing MetaConnect login — no account creation here) */}
          {!authGated && step === 1 && (
            <div data-testid="step-structure">
              <h2 className="font-display text-2xl lg:text-3xl" style={{ color: "#0F2B5B", fontWeight: 600, letterSpacing: "-0.015em" }}>Votre structure</h2>
              <p className="mt-2 text-[14.5px]" style={{ color: "#64748B" }}>Quelques informations sur votre établissement — votre identité personnelle est déjà reprise de votre compte MetaConnect.</p>

              {/* Bandeau SSO — mint accent on light */}
              <div
                className="mt-4 px-4 py-3 flex items-start gap-3"
                style={{ background: "#F0F9FF", border: "0.5px solid #BAE6FD", borderLeft: "3px solid #4FC3F7", borderRadius: "0 8px 8px 0" }}
                data-testid="structure-sso-banner"
              >
                <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#4FC3F7" }} />
                <div className="text-[13px] leading-relaxed" style={{ color: "#0F2B5B" }}>
                  <span style={{ fontWeight: 600 }}>Connecté en tant que {user?.first_name} {user?.last_name}</span>
                  <span style={{ color: "#64748B" }}> · {user?.email} · 2FA actif</span>
                  <div className="text-[11.5px] mt-0.5" style={{ color: "#64748B" }}>Vous utilisez votre compte MetaConnect existant (MSSanté) — aucun second mot de passe nécessaire.</div>
                </div>
              </div>

              <div className="mt-6 grid sm:grid-cols-2 gap-5">
                {[
                  ["name", "Nom de la structure", "Cabinet du Dr Martin / CPTS Loiret Nord…", "text"],
                  ["structureType", "Type de structure", "CPTS, MSP, Cabinet, Clinique…", "select", ["CPTS", "MSP", "Cabinet libéral", "Clinique", "EHPAD", "Groupe médical", "Établissement public", "Autre"]],
                  ["psCount", "Nombre de PS concernés", "Estimation", "select", ["1-5", "6-20", "21-50", "51-200", "200+"]],
                  ["contactPhone", "Téléphone (optionnel)", "06 12 34 56 78", "tel"],
                ].map(([k, lbl, ph, t, opts]) => (
                  <div key={k}>
                    <label className="text-[11px] uppercase tracking-[0.14em] block mb-1.5 font-semibold" style={{ color: "#4FC3F7" }}>{lbl}</label>
                    {t === "select" ? (
                      <select
                        value={brand[k]}
                        onChange={(e) => setBrand({ ...brand, [k]: e.target.value })}
                        className="w-full text-[14px] py-2 px-3"
                        style={{ background: "#ffffff", border: "0.5px solid #CBD5E0", borderRadius: 8, color: "#0F2B5B" }}
                        data-testid={`brand-${k}`}
                      >
                        <option value="">— Sélectionner —</option>
                        {opts.map((o) => <option key={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input
                        type={t}
                        placeholder={ph}
                        value={brand[k]}
                        onChange={(e) => setBrand({ ...brand, [k]: e.target.value })}
                        className="w-full text-[14px] py-2 px-3"
                        style={{ background: "#ffffff", border: "0.5px solid #CBD5E0", borderRadius: 8, color: "#0F2B5B" }}
                        data-testid={`brand-${k}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 2 — IDENTITY */}
          {!authGated && step === 2 && (
            <div data-testid="step-identity">
              <h2 className="font-display text-2xl lg:text-3xl" style={{ color: "#0F2B5B", fontWeight: 600, letterSpacing: "-0.015em" }}>Identité visuelle &amp; domaine</h2>

              {/* Bandeau de contexte simulation */}
              <div
                className="mt-4 px-4 py-3 flex items-start gap-3"
                style={{ background: "#F0F9FF", border: "0.5px solid #BAE6FD", borderLeft: "3px solid #4FC3F7", borderRadius: "0 8px 8px 0" }}
                data-testid="identity-simulation-banner"
              >
                <Info className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#4FC3F7" }} />
                <p className="text-[13px] leading-relaxed" style={{ color: "#0F2B5B" }}>
                  <span style={{ fontWeight: 600 }}>Simulation en cours.</span>{" "}
                  Votre charte sera appliquée à l&apos;aperçu interactif de votre plateforme. Aucun déploiement réel n&apos;est effectué à ce stade.
                </p>
              </div>

              <p className="mt-4 text-[14.5px]" style={{ color: "#64748B" }}>Ces informations seront utilisées pour générer votre aperçu simulé et votre cahier des charges PDF.</p>

              <div className="mt-8 grid lg:grid-cols-2 gap-5">
                {/* Card charte graphique */}
                <div className="p-6" style={{ background: "#ffffff", border: "0.5px solid #E8EDF5", borderRadius: 12, boxShadow: "0 1px 2px rgba(15,43,91,0.04)" }}>
                  <div className="flex items-center gap-2 mb-4">
                    <Palette className="w-4 h-4" style={{ color: "#4FC3F7" }} />
                    <div className="text-[12px] uppercase tracking-[0.14em] font-semibold" style={{ color: "#4FC3F7" }}>Charte graphique</div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[11px] block mb-1" style={{ color: "#64748B" }}>Couleur principale</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={brand.color} onChange={(e) => setBrand({ ...brand, color: e.target.value })} className="w-9 h-9 rounded-md cursor-pointer" style={{ border: "0.5px solid #CBD5E0", background: "transparent" }} data-testid="brand-color" />
                        <span className="text-[12px] font-mono" style={{ color: "#64748B" }}>{brand.color}</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-[11px] block mb-1" style={{ color: "#64748B" }}>Couleur accent</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={brand.accent} onChange={(e) => setBrand({ ...brand, accent: e.target.value })} className="w-9 h-9 rounded-md cursor-pointer" style={{ border: "0.5px solid #CBD5E0", background: "transparent" }} data-testid="brand-accent" />
                        <span className="text-[12px] font-mono" style={{ color: "#64748B" }}>{brand.accent}</span>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <label className="text-[11px] block mb-1" style={{ color: "#64748B" }}>Police</label>
                      <select value={brand.font} onChange={(e) => setBrand({ ...brand, font: e.target.value })} className="w-full text-[13px] py-1.5 px-2" style={{ background: "#ffffff", border: "0.5px solid #CBD5E0", borderRadius: 8, color: "#0F2B5B" }} data-testid="brand-font">
                        <option>Inter</option><option>Poppins</option><option>Plus Jakarta Sans</option><option>DM Sans</option><option>Source Sans Pro</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="text-[11px] block mb-1" style={{ color: "#64748B" }}>Votre logo</label>
                      <div className="flex items-start gap-3">
                        {brand.logoUrl ? (
                          <div className="w-16 h-16 rounded-md grid place-items-center overflow-hidden shrink-0" style={{ border: "0.5px solid #CBD5E0", background: "#F0F9FF" }}>
                            <img src={brand.logoUrl} alt="logo" className="max-w-full max-h-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                          </div>
                        ) : (
                          <div className="w-16 h-16 rounded-md grid place-items-center text-[10px] shrink-0" style={{ border: "1px dashed #CBD5E0", color: "#94A3B8" }}>Logo</div>
                        )}
                        <div className="flex-1 space-y-2">
                          <label
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-[12.5px] cursor-pointer transition-colors"
                            style={{ background: "#ffffff", border: "0.5px solid #CBD5E0", color: "#0F2B5B" }}
                            data-testid="brand-logo-upload-label"
                          >
                            <Download className="w-3.5 h-3.5 rotate-180" />
                            {brand.logoUrl?.startsWith("data:") ? "Remplacer le fichier" : "Téléverser un fichier (PNG/SVG)"}
                            <input
                              type="file"
                              accept="image/png,image/svg+xml,image/jpeg,image/webp"
                              className="hidden"
                              onChange={async (e) => {
                                const f = e.target.files?.[0];
                                if (!f) return;
                                if (f.size > 800_000) { toast.error("Logo trop lourd (max 800 Ko). Compressez-le ou collez une URL."); return; }
                                const reader = new FileReader();
                                reader.onload = () => setBrand((b) => ({ ...b, logoUrl: String(reader.result || "") }));
                                reader.readAsDataURL(f);
                              }}
                              data-testid="brand-logo-file"
                            />
                          </label>
                          <input
                            type="url"
                            placeholder="…ou collez une URL : https://votre-site.fr/logo.png"
                            value={brand.logoUrl?.startsWith("data:") ? "" : brand.logoUrl}
                            onChange={(e) => setBrand({ ...brand, logoUrl: e.target.value })}
                            className="w-full text-[12.5px] py-1.5 px-2"
                            style={{ background: "#ffffff", border: "0.5px solid #CBD5E0", borderRadius: 8, color: "#0F2B5B" }}
                            data-testid="brand-logo"
                          />
                          {brand.logoUrl && (
                            <button type="button" onClick={() => setBrand({ ...brand, logoUrl: "" })} className="text-[11px] underline" style={{ color: "#64748B" }}>Retirer le logo</button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Aperçu macro de la charte */}
                  <div className="mt-5 pt-5" style={{ borderTop: "0.5px solid #E8EDF5" }}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-[11px] uppercase tracking-[0.12em] font-semibold" style={{ color: "#4FC3F7" }}>Aperçu en direct</div>
                      <div className="text-[10.5px]" style={{ color: "#94A3B8" }}>Mise à jour automatique</div>
                    </div>
                    <div className="rounded-xl bg-white overflow-hidden" style={{ boxShadow: "0 8px 24px rgba(15,43,91,0.12)", border: "0.5px solid #E8EDF5", fontFamily: brand.font || "Inter, sans-serif" }} data-testid="brand-live-preview">
                      {/* Header simulé — utilise la couleur principale */}
                      <div className="flex items-center justify-between px-4 py-3" style={{ backgroundColor: brand.color }}>
                        <div className="flex items-center gap-2">
                          {brand.logoUrl ? (
                            <div className="w-7 h-7 rounded bg-white/95 grid place-items-center overflow-hidden">
                              <img src={brand.logoUrl} alt="" className="max-w-full max-h-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            </div>
                          ) : (
                            <div className="w-7 h-7 rounded bg-white/95 grid place-items-center text-[11px] font-bold" style={{ color: brand.color }}>
                              {(brand.name || "MC").slice(0, 2).toUpperCase()}
                            </div>
                          )}
                          <div className="text-white font-semibold text-[12.5px] truncate max-w-[140px]">{brand.name || "Votre cabinet"}</div>
                        </div>
                        <div className="text-white/90 text-[11px] hidden sm:flex items-center gap-3">
                          <span>RDV</span><span>Dossier</span><span>Contact</span>
                        </div>
                      </div>
                      {/* Corps simulé */}
                      <div className="px-4 py-4">
                        <div className="text-[13px] font-semibold text-slate-900">Bonjour Marie 👋</div>
                        <div className="text-[11.5px] text-slate-500 mt-0.5">Votre prochaine téléconsultation est confirmée.</div>

                        <div className="mt-3 flex items-center gap-2">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold text-white" style={{ backgroundColor: brand.color }}>
                            <span className="w-1.5 h-1.5 rounded-full bg-white" /> Téléconsult. confirmée
                          </span>
                          <a href="#" onClick={(e) => e.preventDefault()} className="text-[11px] underline" style={{ color: brand.accent }}>Voir le détail →</a>
                        </div>

                        <div className="mt-4 flex items-center gap-2">
                          <button className="px-3 py-1.5 rounded-md text-[12px] font-semibold text-white shadow-sm" style={{ backgroundColor: brand.color }}>
                            Prendre RDV
                          </button>
                          <button className="px-3 py-1.5 rounded-md text-[12px] font-semibold border" style={{ borderColor: brand.accent, color: brand.accent }}>
                            Voir mes ordonnances
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Légende */}
                    <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] leading-relaxed" style={{ color: "#64748B" }}>
                      <div className="flex items-start gap-2">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0 mt-1" style={{ backgroundColor: brand.color }} />
                        <span><span style={{ color: "#0F2B5B", fontWeight: 600 }}>Principale</span> — en-tête, boutons d&apos;action, badges et avatars patients.</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0 mt-1" style={{ backgroundColor: brand.accent }} />
                        <span><span style={{ color: "#0F2B5B", fontWeight: 600 }}>Accent</span> — liens secondaires, contours, mises en valeur ponctuelles.</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card domaine */}
                <div className="p-6" style={{ background: "#ffffff", border: "0.5px solid #E8EDF5", borderRadius: 12, boxShadow: "0 1px 2px rgba(15,43,91,0.04)" }}>
                  <div className="flex items-center gap-2 mb-4">
                    <Globe className="w-4 h-4" style={{ color: "#4FC3F7" }} />
                    <div className="text-[12px] uppercase tracking-[0.14em] font-semibold" style={{ color: "#4FC3F7" }}>Nom de domaine</div>
                  </div>
                  <div className="flex gap-2 mb-3">
                    {["subdomain", "domain"].map((t) => {
                      const on = brand.domainType === t;
                      return (
                        <button
                          key={t}
                          onClick={() => setBrand({ ...brand, domainType: t })}
                          className="flex-1 py-2 text-[12px] transition-colors"
                          style={{
                            background: on ? "#0F2B5B" : "#ffffff",
                            border: `0.5px solid ${on ? "#0F2B5B" : "#CBD5E0"}`,
                            borderRadius: 8,
                            color: on ? "#ffffff" : "#64748B",
                            fontWeight: on ? 500 : 400,
                          }}
                          data-testid={`domain-type-${t}`}
                        >
                          {t === "subdomain" ? "Sous-domaine MetaConnect" : "Mon nom de domaine (Enterprise)"}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex items-center overflow-hidden" style={{ background: "#ffffff", border: "0.5px solid #CBD5E0", borderRadius: 8 }}>
                    <input
                      type="text"
                      placeholder={brand.domainType === "subdomain" ? "mon-cabinet" : "tele.mon-cabinet.fr"}
                      value={brand.domain}
                      onChange={(e) => setBrand({ ...brand, domain: e.target.value })}
                      className="flex-1 bg-transparent text-[13px] py-2 px-3 outline-none"
                      style={{ color: "#0F2B5B" }}
                      data-testid="brand-domain"
                    />
                    {brand.domainType === "subdomain" && <span className="text-[12px] px-2" style={{ color: "#64748B" }}>.metaconnect.fr</span>}
                  </div>
                  {brand.domainType === "subdomain" && (
                    <p className="mt-2 text-[12px] leading-relaxed" style={{ color: "#64748B" }} data-testid="subdomain-helper">
                      Ce sous-domaine sera visible dans votre aperçu simulé. Il sera activé uniquement après validation de votre devis et signature du contrat.
                    </p>
                  )}
                  <p className="mt-3 text-[12px] leading-relaxed" style={{ color: "#64748B" }}>
                    Si vous choisissez un nom de domaine propre, vous devrez nous transmettre l&apos;accès DNS de votre registrar pour qu&apos;on configure les enregistrements A / CNAME / TLS.
                  </p>
                  <div className="mt-4 pt-4" style={{ borderTop: "0.5px solid #E8EDF5" }}>
                    <label className="text-[11px] uppercase tracking-[0.14em] block mb-1.5 font-semibold" style={{ color: "#4FC3F7" }}>Lien public de prise de RDV (optionnel)</label>
                    <input
                      type="url"
                      placeholder="Laissez vide pour utiliser notre agenda intégré"
                      value={brand.rdvUrl}
                      onChange={(e) => setBrand({ ...brand, rdvUrl: e.target.value })}
                      className="w-full text-[13px] py-1.5 px-2"
                      style={{ background: "#ffffff", border: "0.5px solid #CBD5E0", borderRadius: 8, color: "#0F2B5B" }}
                      data-testid="brand-rdv-url"
                    />
                    <div className="mt-3 p-3" style={{ background: "#F0F9FF", border: "0.5px solid #BAE6FD", borderLeft: "3px solid #4FC3F7", borderRadius: "0 8px 8px 0" }}>
                      <div className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#4FC3F7" }} />
                        <div className="text-[12px] leading-relaxed" style={{ color: "#0F2B5B" }}>
                          <b>Recommandé : utilisez notre agenda intégré.</b> Pour la téléconsultation et la téléexpertise, l&apos;agenda MetaConnect gère automatiquement les <b>disponibilités multi-PS</b> (médecin, IDE, sage-femme…), les <b>prérequis HAS</b> (1<sup>er</sup> soin présentiel, prescription valide), la salle d&apos;attente LiveKit et les <b>emails patients pré-programmés</b>. Un lien externe (Doctolib/Maiia/KelDoc) ne sait pas faire tout ça.
                        </div>
                      </div>
                    </div>
                    <p className="mt-2 text-[11px]" style={{ color: "#64748B" }}>
                      Vous avez tout de même un lien externe à conserver ? Collez-le ci-dessus, il sera affiché sur votre fiche vitrine publique.
                    </p>
                  </div>
                </div>
              </div>

              {/* HDS banner — kept emerald for HDS semantic but light */}
              <div className="mt-6 p-4 flex items-start gap-3" style={{ background: "#ECFDF5", border: "0.5px solid #A7F3D0", borderLeft: "3px solid #10B981", borderRadius: "0 8px 8px 0" }}>
                <Lock className="w-5 h-5 mt-0.5 shrink-0" style={{ color: "#10B981" }} />
                <div>
                  <div className="font-semibold text-[14px]" style={{ color: "#065F46" }}>Hébergement HDS inclus chez notre partenaire certifié 🇫🇷</div>
                  <p className="text-[13px] mt-1" style={{ color: "#047857" }}>MetaConnect héberge votre plateforme chez Scaleway ou OVH (France) — PGSSI-S, RGPD, ISO 27001, Garantie DICA. Aucune démarche à votre charge.</p>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3 — SAVOIR-FAIRE */}
          {!authGated && step === 3 && (
            <div data-testid="step-savoir-faire">
              <h2 className="font-display text-2xl lg:text-3xl" style={{ color: "#0F2B5B", fontWeight: 600, letterSpacing: "-0.015em" }}>
                Notre savoir-faire est <span style={{ color: "#4FC3F7" }}>inclus</span>
              </h2>
              <p className="mt-2 text-[14.5px] max-w-2xl" style={{ color: "#64748B" }}>
                MetaConnect apporte ses briques propriétaires éprouvées sur le terrain : widgets RDV, emails patients, hébergement HDS clé en main. Vous n&apos;avez rien à coder.
              </p>
              <div className="mt-8 grid sm:grid-cols-2 gap-5">
                {SAVOIR_FAIRE.map((f, i) => {
                  const Icon = f.icon;
                  return (
                    <div
                      key={i}
                      className="p-6"
                      style={{ background: "#ffffff", border: "0.5px solid #E8EDF5", borderRadius: 12, boxShadow: "0 1px 2px rgba(15,43,91,0.04)" }}
                    >
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="w-11 h-11 rounded-xl grid place-items-center" style={{ background: "#F0F9FF", border: "0.5px solid #BAE6FD" }}>
                          <Icon className="w-5 h-5" style={{ color: "#0F2B5B" }} />
                        </div>
                        <span
                          className="text-[10px] uppercase px-2 py-0.5 rounded font-semibold"
                          style={{ background: "#F0F9FF", color: "#4FC3F7", border: "0.5px solid #BAE6FD" }}
                        >
                          {f.pill}
                        </span>
                      </div>
                      <div className="font-semibold text-[16px]" style={{ color: "#0F2B5B" }}>{f.title}</div>
                      <p className="text-[13.5px] leading-relaxed mt-2" style={{ color: "#64748B" }}>{f.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* STEP 4 — CONFIGURATION (per relevant block) — B2 palette navy/white/mint */}
          {!authGated && step === 4 && (
            <div
              data-testid="step-config"
              style={{
                background: "#F8FAFC",
                padding: "40px 32px",
                margin: "-48px -20px",
                marginLeft: "calc(-1 * (50vw - 50%))",
                marginRight: "calc(-1 * (50vw - 50%))",
                minHeight: "calc(100vh - 56px)",
              }}
            >
              <div style={{ maxWidth: 1080, margin: "0 auto" }}>
                <h2 style={{ color: "#0F2B5B", fontSize: 28, fontWeight: 600, letterSpacing: "-0.015em", lineHeight: 1.2 }} className="font-display">
                  Paramétrez votre simulation
                </h2>
                <p style={{ color: "#64748B", fontSize: 14, marginTop: 8, lineHeight: 1.55 }}>
                  Vos réponses servent à générer votre aperçu personnalisé et votre cahier des charges. Aucune expertise technique requise — répondez au mieux, tout est ajustable.
                </p>

                {/* Bandeau contextuel inline B2 — border-left mint */}
                <div
                  data-testid="config-simulation-banner"
                  style={{
                    background: "#ffffff",
                    border: "0.5px solid #BAE6FD",
                    borderLeft: "3px solid #4FC3F7",
                    borderRadius: "0 8px 8px 0",
                    padding: "16px 20px",
                    marginTop: 20,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                  }}
                >
                  <Info size={16} style={{ color: "#4FC3F7", marginTop: 2, flexShrink: 0 }} />
                  <p style={{ color: "#0F2B5B", fontSize: 13, lineHeight: 1.55, margin: 0, flex: 1 }}>
                    <span style={{ fontWeight: 600, color: "#0F2B5B" }}>Simulation en cours.</span>{" "}
                    Ces réponses alimentent votre aperçu et votre cahier des charges PDF. Aucune configuration n&apos;est activée en production à ce stade.
                  </p>
                </div>

                {/* Panneau récapitulatif des modules — vue d'ensemble compacte, cliquable pour naviguer */}
                <div
                  data-testid="config-modules-overview"
                  className="mt-6"
                  style={{
                    background: "#ffffff",
                    border: "0.5px solid #E2E8F0",
                    borderRadius: 12,
                    padding: 18,
                    boxShadow: "0 1px 2px rgba(15, 43, 91, 0.04)",
                  }}
                >
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div style={{ color: "#0F2B5B", fontSize: 13, fontWeight: 600, letterSpacing: "-0.005em" }}>
                      Modules de configuration <span style={{ color: "#64748B", fontWeight: 400 }}>· {relevantBlocks.length} au total</span>
                    </div>
                    <div style={{ color: "#64748B", fontSize: 11.5 }}>Cliquez sur un module pour y accéder</div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                    {relevantBlocks.map((bid, idx) => {
                      const b = BLOCKS[bid];
                      const blockAnswers = answers[bid] || {};
                      const answered = b.questions.filter((q) => {
                        const v = blockAnswers[q.id];
                        return Array.isArray(v) ? v.length > 0 : (v !== undefined && v !== null && String(v).trim() !== "");
                      }).length;
                      const total = b.questions.length;
                      const pct = Math.round((answered / total) * 100);
                      const complete = answered === total;
                      const started = answered > 0 && !complete;
                      const IconComp = b.icon;
                      return (
                        <button
                          key={bid}
                          onClick={() => {
                            const el = document.getElementById(`config-block-${bid.toLowerCase()}`);
                            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                          }}
                          data-testid={`overview-tile-${bid.toLowerCase()}`}
                          className="text-left"
                          style={{
                            background: complete ? "#F0F9FF" : "#ffffff",
                            border: `0.5px solid ${complete ? "#4FC3F7" : started ? "#BAE6FD" : "#E2E8F0"}`,
                            borderRadius: 10,
                            padding: "10px 12px",
                            cursor: "pointer",
                            transition: "all .18s ease",
                            display: "flex",
                            gap: 10,
                            alignItems: "flex-start",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = "#4FC3F7";
                            e.currentTarget.style.transform = "translateY(-1px)";
                            e.currentTarget.style.boxShadow = "0 4px 12px -4px rgba(15,43,91,0.08)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = complete ? "#4FC3F7" : started ? "#BAE6FD" : "#E2E8F0";
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "none";
                          }}
                        >
                          <div
                            style={{
                              width: 32, height: 32, borderRadius: 8,
                              background: complete ? "#4FC3F7" : "#F1F5F9",
                              color: complete ? "#ffffff" : "#0F2B5B",
                              display: "grid", placeItems: "center", flexShrink: 0,
                            }}
                          >
                            <IconComp size={16} strokeWidth={1.8} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <span style={{
                                fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
                                color: "#64748B", fontFamily: "ui-monospace, monospace",
                              }}>
                                {String(idx + 1).padStart(2, "0")} · {bid}
                              </span>
                              {complete && <CheckCircle2 size={11} style={{ color: "#4FC3F7" }} />}
                            </div>
                            <div style={{
                              color: "#0F2B5B", fontSize: 12.5, fontWeight: 500,
                              lineHeight: 1.25, letterSpacing: "-0.005em",
                              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                            }} title={b.title}>
                              {b.title}
                            </div>
                            <div style={{
                              marginTop: 6, height: 3, borderRadius: 999,
                              background: "#E2E8F0", overflow: "hidden",
                            }}>
                              <div style={{
                                width: `${pct}%`, height: "100%",
                                background: complete ? "#4FC3F7" : "#0F2B5B",
                                transition: "width .25s ease",
                              }} />
                            </div>
                            <div style={{
                              marginTop: 4, fontSize: 10.5,
                              color: complete ? "#0369A1" : "#64748B", fontWeight: complete ? 600 : 400,
                            }}>
                              {complete ? "Complet" : started ? `${answered} / ${total} réponses` : `${total} question${total > 1 ? "s" : ""}`}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-6 space-y-6">
                  {relevantBlocks.map((bid, idx) => {
                    const b = BLOCKS[bid];
                    const blockAnswers = answers[bid] || {};
                    const answeredCount = b.questions.filter((q) => {
                      const v = blockAnswers[q.id];
                      if (Array.isArray(v)) return v.length > 0;
                      return v !== undefined && v !== null && String(v).trim() !== "";
                    }).length;
                    const totalQ = b.questions.length;
                    const pct = Math.round((answeredCount / totalQ) * 100);
                    const complete = answeredCount === totalQ;
                    const IconComp = b.icon;
                    return (
                      <div
                        key={bid}
                        id={`config-block-${bid.toLowerCase()}`}
                        data-testid={`config-block-${bid.toLowerCase()}`}
                        style={{ scrollMarginTop: 24 }}
                      >
                        {/* En-tête module — navy compact, icône circulaire, chips réglementaires */}
                        <div
                          style={{
                            background: "#0F2B5B",
                            borderRadius: 12,
                            padding: "16px 20px",
                            marginBottom: 0,
                            borderBottomLeftRadius: 0,
                            borderBottomRightRadius: 0,
                          }}
                        >
                          <div className="flex items-start gap-3.5">
                            {/* Icône circulaire — sky sur navy */}
                            <div
                              style={{
                                width: 40, height: 40, borderRadius: 10,
                                background: "rgba(79, 195, 247, 0.14)",
                                border: "1px solid rgba(79, 195, 247, 0.30)",
                                color: "#4FC3F7",
                                display: "grid", placeItems: "center", flexShrink: 0,
                              }}
                            >
                              <IconComp size={20} strokeWidth={1.7} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span style={{
                                  fontSize: 10.5, fontWeight: 700, letterSpacing: "0.14em",
                                  color: "#4FC3F7", fontFamily: "ui-monospace, monospace",
                                  padding: "2px 8px", borderRadius: 999,
                                  background: "rgba(79,195,247,0.10)",
                                  border: "0.5px solid rgba(79,195,247,0.28)",
                                }}>
                                  {String(idx + 1).padStart(2, "0")} · {bid}
                                </span>
                                {b.new && (
                                  <span style={{
                                    padding: "2px 8px", borderRadius: 999,
                                    background: "rgba(110,231,197,0.18)", color: "#6EE7C5",
                                    fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
                                  }}>NOUVEAU</span>
                                )}
                                {complete && (
                                  <span style={{
                                    padding: "2px 8px", borderRadius: 999,
                                    background: "rgba(110,231,197,0.18)", color: "#6EE7C5",
                                    fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
                                    display: "inline-flex", alignItems: "center", gap: 4,
                                  }}>
                                    <CheckCircle2 size={10} /> COMPLET
                                  </span>
                                )}
                              </div>
                              <div style={{
                                color: "#ffffff", fontWeight: 600,
                                fontSize: 17, letterSpacing: "-0.01em",
                                marginTop: 6, lineHeight: 1.25,
                              }} className="font-display">
                                {b.title}
                              </div>
                              <div style={{ color: "#B8D4E8", fontSize: 12.5, marginTop: 3, lineHeight: 1.5 }}>
                                {b.summary}
                              </div>
                              {/* Chips réglementaires */}
                              {b.refs && b.refs.length > 0 && (
                                <div className="mt-2.5 flex flex-wrap gap-1.5">
                                  {b.refs.map((r) => (
                                    <span key={r} style={{
                                      fontSize: 10.5, padding: "3px 9px", borderRadius: 4,
                                      background: "rgba(79,195,247,0.14)",
                                      border: "0.5px solid rgba(79,195,247,0.35)",
                                      color: "#E6F4FE", letterSpacing: "0.03em", fontWeight: 500,
                                    }}>{r}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                            {/* Compteur réponses */}
                            <div className="text-right shrink-0" style={{ minWidth: 90 }}>
                              <div style={{
                                fontSize: 22, fontWeight: 700, color: "#ffffff",
                                lineHeight: 1, letterSpacing: "-0.02em",
                                fontFamily: "ui-monospace, monospace",
                              }}>
                                {answeredCount}<span style={{ color: "#B8D4E8", fontSize: 14 }}>/{totalQ}</span>
                              </div>
                              <div style={{ color: "#B8D4E8", fontSize: 10.5, marginTop: 3, letterSpacing: "0.04em", fontWeight: 500 }}>
                                RÉPONSES
                              </div>
                            </div>
                          </div>
                          {/* Barre progression module — sky sur rgba */}
                          <div style={{
                            background: "rgba(79,195,247,0.16)",
                            height: 3, borderRadius: 2, marginTop: 14, overflow: "hidden",
                          }}>
                            <div style={{ width: `${pct}%`, height: "100%", background: "#4FC3F7", transition: "width .25s ease" }} />
                          </div>
                        </div>

                        {/* Corps module — white card */}
                        <div
                          style={{
                            background: "#ffffff",
                            border: "0.5px solid #E8EDF5",
                            borderTop: 0,
                            borderRadius: 12,
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0,
                            padding: "24px",
                          }}
                        >
                          <div className="space-y-5">
                            {b.questions.map((q) => {
                              const val = blockAnswers[q.id];
                              const setVal = (v) => setAnswer(bid, q.id, v);
                              return (
                                <div key={q.id}>
                                  <div style={{ color: "#0F2B5B", fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
                                    {q.label}
                                    {q.optional && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 400, color: "#94A3B8" }}>(optionnel)</span>}
                                  </div>
                                  {q.helper && (
                                    <p style={{ color: "#64748B", fontSize: 12, fontStyle: "italic", marginBottom: 12, lineHeight: 1.5 }}>{q.helper}</p>
                                  )}
                                  {q.type === "radio" && (
                                    <div className="flex flex-wrap gap-2">
                                      {q.options.map((o) => {
                                        const on = val === o;
                                        return (
                                          <button
                                            key={o}
                                            onClick={() => setVal(o)}
                                            data-testid={`q-${q.id}-${o}`}
                                            style={{
                                              background: on ? "#0F2B5B" : "#ffffff",
                                              color: on ? "#ffffff" : "#64748B",
                                              border: `0.5px solid ${on ? "#0F2B5B" : "#CBD5E0"}`,
                                              borderRadius: 20,
                                              padding: "8px 18px",
                                              fontSize: 13,
                                              fontWeight: on ? 500 : 400,
                                              cursor: "pointer",
                                              transition: "all .15s ease",
                                            }}
                                            className="hover:!bg-[#F0F9FF] hover:!border-[#4FC3F7] hover:!text-[#0F2B5B]"
                                            onMouseEnter={(e) => { if (!on) { e.currentTarget.style.background = "#F0F9FF"; e.currentTarget.style.borderColor = "#4FC3F7"; e.currentTarget.style.color = "#0F2B5B"; } }}
                                            onMouseLeave={(e) => { if (!on) { e.currentTarget.style.background = "#ffffff"; e.currentTarget.style.borderColor = "#CBD5E0"; e.currentTarget.style.color = "#64748B"; } }}
                                          >
                                            {o}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                  {q.type === "checkbox" && (
                                    <div className="flex flex-col gap-2">
                                      {q.options.map((o) => {
                                        const arr = Array.isArray(val) ? val : [];
                                        const on = arr.includes(o);
                                        return (
                                          <button
                                            key={o}
                                            onClick={() => setVal(on ? arr.filter((x) => x !== o) : [...arr, o])}
                                            data-testid={`q-${q.id}-${o}`}
                                            style={{
                                              background: on ? "#F0F9FF" : "#ffffff",
                                              color: on ? "#0F2B5B" : "#64748B",
                                              border: `0.5px solid ${on ? "#4FC3F7" : "#E2E8F0"}`,
                                              borderRadius: 8,
                                              padding: "10px 14px",
                                              fontSize: 13,
                                              fontWeight: on ? 500 : 400,
                                              textAlign: "left",
                                              cursor: "pointer",
                                              display: "inline-flex",
                                              alignItems: "center",
                                              gap: 10,
                                              transition: "all .15s ease",
                                            }}
                                          >
                                            <span style={{
                                              width: 16, height: 16, borderRadius: 4,
                                              border: `1px solid ${on ? "#4FC3F7" : "#CBD5E0"}`,
                                              background: on ? "#4FC3F7" : "#ffffff",
                                              display: "grid", placeItems: "center", flexShrink: 0,
                                            }}>
                                              {on && <Check size={11} strokeWidth={3} color="#ffffff" />}
                                            </span>
                                            {o}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                  {(q.type === "text" || q.type === "textarea") && (
                                    q.type === "textarea" ? (
                                      <textarea
                                        value={val || ""}
                                        onChange={(e) => setVal(e.target.value)}
                                        rows={3}
                                        style={{
                                          width: "100%", background: "#ffffff",
                                          border: "0.5px solid #CBD5E0", borderRadius: 8,
                                          padding: "10px 14px", fontSize: 13, color: "#0F2B5B",
                                          outline: "none", resize: "vertical",
                                          fontFamily: "inherit",
                                        }}
                                        onFocus={(e) => { e.currentTarget.style.borderColor = "#4FC3F7"; e.currentTarget.style.boxShadow = "0 0 0 3px rgba(79,195,247,0.15)"; }}
                                        onBlur={(e) => { e.currentTarget.style.borderColor = "#CBD5E0"; e.currentTarget.style.boxShadow = "none"; }}
                                      />
                                    ) : (
                                      <input
                                        type="text"
                                        value={val || ""}
                                        onChange={(e) => setVal(e.target.value)}
                                        style={{
                                          width: "100%", background: "#ffffff",
                                          border: "0.5px solid #CBD5E0", borderRadius: 8,
                                          padding: "10px 14px", fontSize: 13, color: "#0F2B5B",
                                          outline: "none",
                                        }}
                                        onFocus={(e) => { e.currentTarget.style.borderColor = "#4FC3F7"; e.currentTarget.style.boxShadow = "0 0 0 3px rgba(79,195,247,0.15)"; }}
                                        onBlur={(e) => { e.currentTarget.style.borderColor = "#CBD5E0"; e.currentTarget.style.boxShadow = "none"; }}
                                      />
                                    )
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Bouton "Passer" text-only — sous la liste */}
                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
                    style={{ background: "transparent", border: 0, color: "#94A3B8", fontSize: 12, cursor: "pointer" }}
                    data-testid="skip-config-btn"
                  >
                    Passer cette étape →
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* STEP 5 — RECAP — C3 split 50/50 palette navy/white/mint */}
          {!authGated && step === 5 && (
            <div
              data-testid="step-recap"
              style={{
                background: "#F8FAFC",
                margin: "-48px -20px",
                marginLeft: "calc(-1 * (50vw - 50%))",
                marginRight: "calc(-1 * (50vw - 50%))",
                minHeight: "calc(100vh - 56px)",
              }}
            >
              <div className="grid lg:grid-cols-2" style={{ minHeight: "calc(100vh - 56px)" }}>
                {/* LEFT — Navy column */}
                <div
                  style={{
                    background: "#0F2B5B",
                    padding: "44px 36px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                  data-testid="recap-left-col"
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignSelf: "flex-start",
                      background: "#4FC3F7",
                      color: "#0F2B5B",
                      borderRadius: 999,
                      padding: "5px 14px",
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                    }}
                  >
                    Étape 6 · Récapitulatif
                  </span>

                  <h1
                    className="font-display"
                    style={{
                      color: "#ffffff",
                      fontSize: "clamp(28px, 3.6vw, 40px)",
                      fontWeight: 600,
                      letterSpacing: "-0.015em",
                      lineHeight: 1.12,
                      marginTop: 20,
                    }}
                  >
                    Votre cahier des charges est <span style={{ color: "#4FC3F7" }}>prêt</span>.
                  </h1>

                  <p style={{ color: "#93B4D4", fontSize: 14.5, marginTop: 14, lineHeight: 1.55, maxWidth: 420 }}>
                    Voici le résumé de vos choix pour <span style={{ color: "#ffffff", fontWeight: 500 }}>{brand.name || "votre structure"}</span>. Téléchargez le PDF ou lancez la mise en production.
                  </p>

                  {/* Bullets résumé choix */}
                  <ul style={{ marginTop: 26, display: "flex", flexDirection: "column", gap: 10, padding: 0, listStyle: "none" }}>
                    {[
                      ["Structure", `${brand.name || "—"} (${brand.structureType || "n/c"}, ${brand.psCount || "n/c"} PS)`],
                      ["Services", services.length ? services.map((id) => SERVICES.find((x) => x.id === id)?.title).filter(Boolean).join(" · ") : "Aucun service sélectionné"],
                      ["Modules configurés", `${relevantBlocks.length} modules — ${answeredQuestions}/${totalQuestions} questions renseignées`],
                      ["Domaine", brand.domain ? `${brand.domain}${brand.domainType === "subdomain" ? ".metaconnect.fr" : ""}` : "à définir"],
                    ].map(([k, v], i) => (
                      <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                        <span
                          aria-hidden="true"
                          style={{ width: 6, height: 6, borderRadius: "50%", background: "#4FC3F7", marginTop: 8, flexShrink: 0 }}
                        />
                        <span style={{ color: "#B8D4E8", fontSize: 13, lineHeight: 1.5 }}>
                          <span style={{ color: "#ffffff", fontWeight: 500 }}>{k} :</span> {v}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA download PDF */}
                  <div className="mt-9 flex flex-wrap items-center gap-3">
                    <button
                      onClick={handleExport}
                      disabled={submitting}
                      data-testid="export-cdc-btn"
                      style={{
                        background: "#4FC3F7",
                        color: "#0F2B5B",
                        fontWeight: 500,
                        border: 0,
                        borderRadius: 8,
                        padding: "12px 22px",
                        fontSize: 14,
                        cursor: submitting ? "wait" : "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        boxShadow: "0 12px 28px rgba(79,195,247,0.32)",
                        opacity: submitting ? 0.7 : 1,
                      }}
                    >
                      {submitting ? (
                        <><Loader2 size={15} className="animate-spin" />Génération du PDF…</>
                      ) : submitted ? (
                        <><Check size={15} />Renvoyer le CDC par email</>
                      ) : (
                        <><Download size={15} />Télécharger le PDF</>
                      )}
                    </button>
                    <button
                      onClick={() => { setStep(0); setSubmitted(false); setPreviewId(null); }}
                      style={{
                        background: "transparent",
                        border: 0,
                        color: "#93B4D4",
                        fontSize: 12,
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                      data-testid="recap-restart-btn"
                    >
                      Recommencer
                    </button>
                  </div>

                  {submitted && (
                    <p style={{ color: "#4FC3F7", fontSize: 12, marginTop: 14 }}>
                      ✓ CDC envoyé à votre adresse email — un expert MetaConnect vous recontacte sous 24-48h.
                    </p>
                  )}
                </div>

                {/* RIGHT — White column */}
                <div
                  style={{
                    background: "#ffffff",
                    borderLeft: "0.5px solid #E8EDF5",
                    padding: "36px 32px",
                  }}
                  data-testid="recap-right-col"
                >
                  {/* Header next steps */}
                  <div style={{ borderLeft: "3px solid #4FC3F7", paddingLeft: 14, marginBottom: 26 }}>
                    <div style={{ color: "#4FC3F7", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                      Prochaines étapes
                    </div>
                    <div style={{ color: "#0F2B5B", fontSize: 15, fontWeight: 500, marginTop: 4 }}>
                      Concrétisez votre plateforme en 3 étapes.
                    </div>
                  </div>

                  {/* Numbered steps */}
                  <ol style={{ display: "flex", flexDirection: "column", gap: 16, padding: 0, listStyle: "none", margin: 0 }}>
                    {[
                      { t: "Validez votre aperçu sandbox", d: "Ouvrez votre plateforme générée et explorez chaque module avec vos couleurs et votre logo." },
                      { t: "Échangez avec un expert MetaConnect", d: "30 minutes pour confirmer le périmètre, les délais et le devis ferme." },
                      { t: "Mise en production sous 4-6 semaines", d: "Configuration HDS, raccordement ANS, formation PS, go-live progressif." },
                    ].map((s, i) => (
                      <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 14 }} data-testid={`recap-next-step-${i + 1}`}>
                        <span
                          style={{
                            width: 28, height: 28, borderRadius: "50%",
                            background: "#0F2B5B", color: "#ffffff",
                            display: "grid", placeItems: "center",
                            fontSize: 13, fontWeight: 600, flexShrink: 0,
                          }}
                        >
                          {i + 1}
                        </span>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: "#0F2B5B", fontWeight: 500, fontSize: 14 }}>{s.t}</div>
                          <div style={{ color: "#64748B", fontSize: 12, marginTop: 3, lineHeight: 1.5 }}>{s.d}</div>
                        </div>
                      </li>
                    ))}
                  </ol>

                  {/* PDF miniature */}
                  <div
                    style={{
                      background: "#F0F9FF",
                      border: "0.5px solid #BAE6FD",
                      borderRadius: 8,
                      padding: 16,
                      marginTop: 26,
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                    }}
                    data-testid="recap-pdf-mini"
                  >
                    <div
                      style={{
                        width: 44, height: 56, background: "#ffffff", borderRadius: 4,
                        border: "0.5px solid #BAE6FD",
                        display: "grid", placeItems: "center", flexShrink: 0,
                        position: "relative", overflow: "hidden",
                      }}
                    >
                      <span style={{ position: "absolute", top: 8, left: 6, right: 6, height: 1.5, background: "#0F2B5B", borderRadius: 1 }} />
                      <span style={{ position: "absolute", top: 14, left: 6, right: 12, height: 1, background: "#CBD5E0", borderRadius: 1 }} />
                      <span style={{ position: "absolute", top: 19, left: 6, right: 16, height: 1, background: "#CBD5E0", borderRadius: 1 }} />
                      <span style={{ position: "absolute", top: 24, left: 6, right: 10, height: 1, background: "#CBD5E0", borderRadius: 1 }} />
                      <span style={{ position: "absolute", bottom: 6, left: 6, padding: "1px 4px", background: "#4FC3F7", color: "#0F2B5B", fontSize: 6, fontWeight: 700, borderRadius: 2 }}>PDF</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: "#0F2B5B", fontSize: 13, fontWeight: 500 }}>
                        Cahier des charges — {brand.name || "votre projet"}
                      </div>
                      <div style={{ color: "#64748B", fontSize: 11.5, marginTop: 2 }}>
                        {relevantBlocks.length} modules · {totalQuestions} questions · conformité ANS / HDS / RGPD
                      </div>
                    </div>
                  </div>

                  {/* CTAs */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 22 }}>
                    <button
                      onClick={handlePreview}
                      disabled={previewLoading}
                      data-testid="open-preview-btn"
                      style={{
                        background: "#0F2B5B",
                        color: "#ffffff",
                        width: "100%",
                        borderRadius: 8,
                        padding: "12px 16px",
                        fontSize: 14,
                        fontWeight: 500,
                        border: 0,
                        cursor: previewLoading ? "wait" : "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                        opacity: previewLoading ? 0.7 : 1,
                        transition: "all .15s ease",
                      }}
                    >
                      {previewLoading ? (
                        <><Loader2 size={15} className="animate-spin" />Génération…</>
                      ) : (
                        <><Sparkles size={15} />Lancer la mise en production<ArrowRight size={14} /></>
                      )}
                    </button>
                    <Link
                      to="/demo"
                      data-testid="recap-talk-expert"
                      style={{
                        background: "#EFF6FF",
                        border: "0.5px solid #BFDBFE",
                        color: "#0F2B5B",
                        width: "100%",
                        borderRadius: 8,
                        padding: "11px 16px",
                        fontSize: 13.5,
                        fontWeight: 500,
                        textDecoration: "none",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 6,
                      }}
                    >
                      Parler à un expert <ArrowRight size={13} />
                    </Link>
                    {previewId && (
                      <button
                        type="button"
                        onClick={() => {
                          const url = `${window.location.origin}/studio/preview/${previewId}`;
                          navigator.clipboard?.writeText(url);
                          toast.success("Lien copié — partagez-le.");
                        }}
                        data-testid="copy-preview-link-btn"
                        style={{
                          background: "transparent",
                          border: 0,
                          color: "#64748B",
                          fontSize: 12,
                          cursor: "pointer",
                          textDecoration: "underline",
                          marginTop: 2,
                        }}
                      >
                        Copier le lien de partage
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation buttons — STICKY FOOTER always visible */}
          {!authGated && step < STEPS.length - 1 && (
            <div
              data-testid="studio-sticky-nav"
              style={{
                position: "fixed",
                left: 0, right: 0, bottom: 0,
                zIndex: 40,
                background: "rgba(255,255,255,0.97)",
                backdropFilter: "blur(12px)",
                borderTop: "0.5px solid #E2E8F0",
                boxShadow: "0 -8px 24px -12px rgba(15,43,91,0.12)",
                padding: "12px 20px",
              }}
            >
              <div style={{ maxWidth: 1180, margin: "0 auto" }}
                   className="flex items-center gap-3 flex-wrap sm:flex-nowrap">
                {/* Progression indicator */}
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: "#0F2B5B", color: "#ffffff",
                    display: "grid", placeItems: "center",
                    fontWeight: 700, fontSize: 13,
                    fontFamily: "ui-monospace, monospace",
                    flexShrink: 0,
                  }}>
                    {step + 1}<span style={{ color: "#4FC3F7" }}>/{STEPS.length}</span>
                  </div>
                  <div className="min-w-0">
                    <div style={{
                      fontSize: 10.5, fontWeight: 700, letterSpacing: "0.14em",
                      color: "#64748B", textTransform: "uppercase",
                    }}>
                      Étape en cours
                    </div>
                    <div style={{
                      fontSize: 14, fontWeight: 600, color: "#0F2B5B",
                      letterSpacing: "-0.005em", lineHeight: 1.15,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {STEPS[step]?.label}
                      {!canNext && (
                        <span style={{ color: "#D97706", fontSize: 12, fontWeight: 500, marginLeft: 8 }}>
                          {step === 0 && "· sélectionnez au moins un service"}
                          {step === 1 && "· complétez les informations"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Progress bar (inline, hidden on very small) */}
                <div className="hidden md:block" style={{ width: 180, flexShrink: 0 }}>
                  <div style={{ background: "#E2E8F0", height: 4, borderRadius: 999, overflow: "hidden" }}>
                    <div style={{
                      width: `${((step + 1) / STEPS.length) * 100}%`,
                      height: "100%", background: "#4FC3F7",
                      transition: "width .25s ease",
                    }} />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 shrink-0">
                  <Link
                    to="/support-distant"
                    data-testid="expert-cta"
                    className="hidden sm:inline-flex"
                    style={{
                      background: "#F1F5F9",
                      color: "#0F2B5B",
                      border: 0,
                      borderRadius: 8,
                      padding: "9px 14px",
                      fontSize: 12.5, fontWeight: 500,
                      alignItems: "center", gap: 6,
                      textDecoration: "none",
                    }}
                  >
                    <Headphones className="w-4 h-4" />
                    Parler à un expert
                  </Link>
                  <button
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    disabled={step === 0}
                    style={{
                      background: "#ffffff",
                      border: "0.5px solid #CBD5E0",
                      color: "#64748B",
                      borderRadius: 8,
                      padding: "9px 14px",
                      fontSize: 13, fontWeight: 500,
                      cursor: step === 0 ? "not-allowed" : "pointer",
                      opacity: step === 0 ? 0.4 : 1,
                      display: "inline-flex", alignItems: "center", gap: 6,
                    }}
                    data-testid="prev-btn"
                  >
                    <ChevronLeft className="w-4 h-4" /> Précédent
                  </button>
                  <button
                    onClick={() => { setStep((s) => Math.min(STEPS.length - 1, s + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    disabled={!canNext}
                    style={{
                      background: canNext ? "#0F2B5B" : "#E2E8F0",
                      color: canNext ? "#ffffff" : "#94A3B8",
                      border: 0,
                      borderRadius: 8,
                      padding: "9px 22px",
                      fontSize: 13.5, fontWeight: 600,
                      cursor: canNext ? "pointer" : "not-allowed",
                      boxShadow: canNext ? "0 8px 22px -10px rgba(15,43,91,0.55)" : "none",
                      display: "inline-flex", alignItems: "center", gap: 6,
                    }}
                    data-testid="next-btn"
                  >
                    {step === STEPS.length - 2 ? "Voir le récap" : "Suivant"}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Spacer to prevent content being hidden by the sticky nav */}
          {!authGated && step < STEPS.length - 1 && <div style={{ height: 88 }} aria-hidden="true" />}

        </div>
      </section>
    </div>
  );
}
